import { useState } from "react";
import { OctagonX, Play } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useAccionarFreno,
  useFrenos,
  type CarrilFrenable,
} from "@/hooks/useFrenos";

/**
 * Freno de mano de los carriles automáticos.
 *
 * La madrugada del 10-sep-2026 hubo que parar el reinicio automático de
 * agencias con 31 routers en rojo, y la única forma fue entrar por SSH al
 * servidor a editar el archivo de entorno. Esto es para que no vuelva a pasar:
 * **parar es un botón, y no pregunta nada.**
 *
 * La asimetría es intencional. Frenar: un clic. Soltar: pide confirmación y es
 * de super_user. Y soltar **no enciende** el carril — solo deja de pararlo.
 */
export function FrenoDeMano({ esSuperUser }: { esSuperUser: boolean }) {
  const { data } = useFrenos();
  const carriles = data?.carriles ?? [];
  if (carriles.length === 0) return null;

  return (
    <section className="rounded-lg border border-border p-4">
      <h2 className="mb-1 text-sm font-semibold">Freno de mano</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Detiene en caliente los carriles que actúan solos sobre equipos del
        cliente. Soltar el freno no los enciende: eso sigue siendo un
        despliegue.
      </p>
      <div className="space-y-3">
        {carriles.map((c) => (
          <Carril key={c.carril} carril={c} esSuperUser={esSuperUser} />
        ))}
      </div>
    </section>
  );
}

function Carril({
  carril,
  esSuperUser,
}: {
  carril: CarrilFrenable;
  esSuperUser: boolean;
}) {
  const [motivo, setMotivo] = useState("");
  const [confirmandoSoltar, setConfirmandoSoltar] = useState(false);
  const accionar = useAccionarFreno();

  if (carril.frenado) {
    return (
      <div className="rounded border border-red-600/40 bg-red-600/5 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-600">
              FRENADO — {carril.descripcion}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {carril.motivo}
              {carril.puesto_por ? ` · lo puso ${carril.puesto_por}` : ""}
            </p>
          </div>
          {esSuperUser &&
            (confirmandoSoltar ? (
              <div className="flex shrink-0 gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    accionar.mutate({ carril: carril.carril, frenar: false });
                    setConfirmandoSoltar(false);
                  }}
                >
                  <Play className="mr-1 h-3 w-3" /> Sí, soltar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmandoSoltar(false)}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => setConfirmandoSoltar(true)}
              >
                Soltar freno
              </Button>
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded border border-border p-3">
      <span className="min-w-0 flex-1 truncate text-sm">
        {carril.descripcion}
      </span>
      <Input
        className="h-8 w-56"
        placeholder="Motivo (opcional)"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
      />
      {/* Sin confirmación y sin permisos especiales: en una emergencia, pedir
          de más es como no tener freno. */}
      <Button
        size="sm"
        variant="destructive"
        disabled={accionar.isPending}
        onClick={() =>
          accionar.mutate({ carril: carril.carril, frenar: true, motivo })
        }
      >
        <OctagonX className="mr-1 h-3 w-3" /> Frenar
      </Button>
    </div>
  );
}
